const ELEVENLABS_API_KEY = process.env.VUE_APP_ELEVENLABS_API_KEY || '';
const ELEVENLABS_VOICE_ID = process.env.VUE_APP_ELEVENLABS_VOICE_ID || '';

export default class ElevenLabsAudio {
  constructor() {
    this.audioCtx = null;
    this.audioEl = null;
    this.analyser = null;
    this.queue = [];
    this.playing = false;
    this.stopped = false;
    this._onStateChange = null;
    console.log('[ElevenLabs] Init — API key:', ELEVENLABS_API_KEY ? ELEVENLABS_API_KEY.substring(0, 6) + '...' : 'MISSING', '| Voice ID:', ELEVENLABS_VOICE_ID || 'MISSING');
  }

  onStateChange(cb) {
    this._onStateChange = cb;
  }

  _notify(isSpeaking) {
    if (this._onStateChange) {
      this._onStateChange(isSpeaking, isSpeaking ? this.analyser : null);
    }
  }

  _ensureAudio() {
    if (this.audioEl) return this.audioEl;

    const audio = new Audio();
    this.audioEl = audio;

    const ctx = new AudioContext();
    this.audioCtx = ctx;

    const source = ctx.createMediaElementSource(audio);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 256;
    analyser.smoothingTimeConstant = 0.8;
    source.connect(analyser);
    analyser.connect(ctx.destination);
    this.analyser = analyser;

    return audio;
  }

  _splitSentences(text) {
    // Split on sentence boundaries, keeping non-empty segments
    return text
      .split(/(?<=[.!?])\s+/)
      .map(s => s.trim())
      .filter(s => s.length > 0);
  }

  queueSpeak(text) {
    if (!text || !text.trim()) return;
    if (!ELEVENLABS_API_KEY || !ELEVENLABS_VOICE_ID) {
      console.warn('[ElevenLabs] Missing API key or voice ID. Key:', ELEVENLABS_API_KEY ? 'set' : 'MISSING', 'Voice:', ELEVENLABS_VOICE_ID ? 'set' : 'MISSING');
      return;
    }
    this.stopped = false;

    const sentences = this._splitSentences(text);
    for (const sentence of sentences) {
      this.queue.push(sentence);
    }

    if (!this.playing) {
      this._processQueue();
    }
  }

  async _processQueue() {
    if (this.queue.length === 0 || this.stopped) {
      this.playing = false;
      this._notify(false);
      return;
    }

    this.playing = true;
    const text = this.queue.shift();

    try {
      console.log('[ElevenLabs] Requesting TTS for:', text.substring(0, 60) + (text.length > 60 ? '...' : ''));
      const res = await fetch(
        `https://api.elevenlabs.io/v1/text-to-speech/${ELEVENLABS_VOICE_ID}/stream?output_format=mp3_22050_32`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'xi-api-key': ELEVENLABS_API_KEY,
          },
          body: JSON.stringify({
            text,
            model_id: 'eleven_turbo_v2_5',
          }),
        }
      );

      console.log('[ElevenLabs] Response status:', res.status);
      if (!res.ok || this.stopped) {
        if (!res.ok) {
          const errText = await res.text().catch(() => 'unknown');
          console.warn('[ElevenLabs] TTS fetch failed:', res.status, errText);
        }
        this._processQueue();
        return;
      }

      const blob = await res.blob();
      if (this.stopped) {
        this._processQueue();
        return;
      }

      const url = URL.createObjectURL(blob);
      const audio = this._ensureAudio();

      // Resume AudioContext if suspended (browser autoplay policy)
      if (this.audioCtx && this.audioCtx.state === 'suspended') {
        try {
          await this.audioCtx.resume();
        } catch (e) {
          console.warn('[ElevenLabs] Failed to resume AudioContext:', e);
        }
      }

      audio.src = url;
      this._notify(true);

      await new Promise((resolve) => {
        audio.onended = () => {
          URL.revokeObjectURL(url);
          resolve();
        };
        audio.onerror = (e) => {
          console.warn('[ElevenLabs] playback error:', e);
          URL.revokeObjectURL(url);
          resolve();
        };
        audio.play().catch((e) => {
          console.warn('[ElevenLabs] play() rejected:', e);
          resolve();
        });
      });

      this._processQueue();
    } catch (e) {
      console.warn('[ElevenLabs] error:', e);
      this._processQueue();
    }
  }

  stop() {
    this.stopped = true;
    this.queue = [];
    if (this.audioEl) {
      this.audioEl.pause();
      this.audioEl.currentTime = 0;
    }
    this.playing = false;
    this._notify(false);
  }

  destroy() {
    this.stop();
    if (this.audioCtx) {
      this.audioCtx.close().catch(() => {});
      this.audioCtx = null;
    }
    this.audioEl = null;
    this.analyser = null;
  }
}
